import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { format } from 'date-fns';
import { Edit3, FileText, Save, X, ChevronDown, ChevronUp, Upload, Copy, ExternalLink, Loader2, RefreshCw, Crop } from 'lucide-react';
import Loader from '../../../components/common/Loader/Loader';
import NewsCard from './NewsCard';
import Toast from '../../../components/common/Toast/Toast';
import ImageCropper from '../../../components/common/ImageCropper/ImageCropper';
import HashtagPicker from '../../../components/common/HashtagPicker/HashtagPicker';
import api from '../../../utils/api';
import { newsService } from '../../../services/news.service';
import { devService } from '../../../services/dev.service';
import { useAuth } from '../../../context/AuthContext';
import './NewsDetail.css';
import { DEFAULT_LANGUAGE } from '@shared/languages.js';

// Category-wise dummy image URLs (using placeholder services with category-specific images)
const CATEGORY_IMAGES = {
  National: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451199_757.jpg',
  Business: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446926_903.jpg',
  Sports: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447376_94.jpg',
  World: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446584_697.jpg',
  Politics: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451612_74.jpg',
  Technology: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
  Startup: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322452049_264.jpg',
  Entertainment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448662_37.jpg',
  Miscellaneous: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg',
  Hatke: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
  Science: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450268_226.jpg',
  Automobile: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449000_634.jpg',
  Education: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449426_933.jpg',
  Environment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322445045_502.jpg',
  Travel: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448299_383.jpg',
  Fashion: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447765_483.jpg',
};

const DEFAULT_IMAGE = 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg';

const getNewsImage = (news) => {
  if (news.imageUrl) return news.imageUrl;
  // Use first category from categories array for image selection
  const firstCategory = news.categories && news.categories.length > 0 ? news.categories[0] : news.category;
  return CATEGORY_IMAGES[firstCategory] || DEFAULT_IMAGE;
};

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

const countChars = (text) => {
  if (!text) return 0;
  return text.length;
};

// Word-level diff function to highlight added and removed words
const getWordDiff = (oldText, newText) => {
  const oldWords = oldText.split(' ');
  const newWords = newText.split(' ');

  const result = [];
  let oldIndex = 0;
  let newIndex = 0;

  while (oldIndex < oldWords.length || newIndex < newWords.length) {
    const oldWord = oldWords[oldIndex];
    const newWord = newWords[newIndex];

    if (oldWord === newWord) {
      result.push({ text: oldWord, type: 'same' });
      oldIndex++;
      newIndex++;
    } else if (oldIndex < oldWords.length && newIndex < newWords.length) {
      // Check if old word exists in future of new words
      const oldInNewFuture = newWords.slice(newIndex).includes(oldWord);
      // Check if new word exists in future of old words
      const newInOldFuture = oldWords.slice(oldIndex).includes(newWord);

      if (oldInNewFuture && !newInOldFuture) {
        result.push({ text: newWord, type: 'added' });
        newIndex++;
      } else if (!oldInNewFuture && newInOldFuture) {
        result.push({ text: oldWord, type: 'removed' });
        oldIndex++;
      } else {
        // Both are different, treat as replacement
        result.push({ text: oldWord, type: 'removed' });
        result.push({ text: newWord, type: 'added' });
        oldIndex++;
        newIndex++;
      }
    } else if (oldIndex < oldWords.length) {
      result.push({ text: oldWord, type: 'removed' });
      oldIndex++;
    } else if (newIndex < newWords.length) {
      result.push({ text: newWord, type: 'added' });
      newIndex++;
    }
  }

  return result;
};

const NewsDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { categories } = useOutletContext() || {};
  const { user } = useAuth();
  const hasFetchedRef = useRef(false);
  const headlineRef = useRef(null);
  const summaryRef = useRef(null);
  const [news, setNews] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ imageUrl: '', headline: '', summary: '', categories: [], hashtags: [], articleText: '' });
  const [saving, setSaving] = useState(false);
  const [expandedTimelineItems, setExpandedTimelineItems] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showChangesOverlay, setShowChangesOverlay] = useState(false);
  const [currentChanges, setCurrentChanges] = useState(null);
  const [toast, setToast] = useState(null);
  const [newsDecks, setNewsDecks] = useState([]);
  const [currentDeckId, setCurrentDeckId] = useState(null);
  const lastProcessedNewsId = useRef(null);
  const [editReason, setEditReason] = useState('');
  const [editReasonError, setEditReasonError] = useState('');

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Add-image-by-URL state
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [loadingUrlImage, setLoadingUrlImage] = useState(false);

  // Address of the image the cropper is open on, or null when it is closed
  const [cropSource, setCropSource] = useState(null);

  // Copy Inshorts URL state
  const [copiedInshortsUrl, setCopiedInshortsUrl] = useState(false);

  // Raw Inshorts data state
  const [rawInshortsData, setRawInshortsData] = useState(null);
  const [loadingRawData, setLoadingRawData] = useState(false);
  const [isDeveloperSectionExpanded, setIsDeveloperSectionExpanded] = useState(false);

  // Inshorts API logs state (Developer Options - API logs part)
  const [isInshortsLogsExpanded, setIsInshortsLogsExpanded] = useState(false);
  const [inshortsLogs, setInshortsLogs] = useState([]);
  const [loadingInshortsLogs, setLoadingInshortsLogs] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [copiedLogId, setCopiedLogId] = useState(null);

  // Custom tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'matched' });

  // Get news data from location state (passed from dashboard) or fetch it
  useEffect(() => {
    // Reset ref when id changes
    hasFetchedRef.current = false;

    const fetchNews = async () => {
      // Prevent duplicate API calls (React Strict Mode can cause double effect runs)
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;

      const newsId = id || location.state?.newsData?._id;

      // Get current deck ID from URL search params or location state
      const searchParams = new URLSearchParams(location.search);
      const deckIdFromUrl = searchParams.get('deckId');

      if (deckIdFromUrl) {
        setCurrentDeckId(deckIdFromUrl);
      } else if (location.state?.currentDeckId) {
        setCurrentDeckId(location.state.currentDeckId);
      }

      if (location.state?.newsData) {
        setNews(location.state.newsData);
        setEditHistory(location.state.newsData.editHistory || []);
        // Fetch decks for this news
        await fetchNewsDecks(newsId);
      } else if (newsId) {
        fetchNewsById(newsId);
      }
    };

    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only depend on id, not location.state

  const fetchNewsById = async (newsId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/news/${newsId}`);
      setNews(response.data.news);
      setEditHistory(response.data.editHistory || []);

      // Fetch decks for this news
      await fetchNewsDecks(newsId);
    } catch (err) {
      setError('Failed to fetch shorts details');
      console.error('Error fetching shorts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsDecks = async (newsId) => {
    try {
      const response = await newsService.getNewsDecks(newsId);
      const decks = response.data?.decks || response.data || [];
      setNewsDecks(Array.isArray(decks) ? decks : []);
      lastProcessedNewsId.current = newsId;
    } catch (err) {
      console.error('Error fetching news decks:', err);
      setNewsDecks([]);
    }
  };

  // Check for edit query parameter to start in edit mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true' && news && !isEditing) {
      handleEdit();
    }
  }, [location.search, news, isEditing]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd/MM/yy HH:mm');
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
  };

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      // Calculate lines based on content length and word wrapping
      const avgCharsPerLine = 80;
      const lineHeight = 22; // Approximate line height
      const contentLines = Math.ceil(textarea.value.length / avgCharsPerLine);
      const newlineLines = textarea.value.split('\n').length - 1;
      const totalLines = Math.max(1, contentLines + newlineLines);
      const minHeight = textarea.id === 'summary-textarea' ? 176 : 50;

      // Set height directly
      textarea.style.height = Math.max(minHeight, totalLines * lineHeight) + 'px';
    }
  };

  // Get changes between original news and current editForm
  const getChanges = () => {
    const changes = {};
    const original = news;
    const current = editForm;

    if (current.imageUrl && current.imageUrl !== original.imageUrl) {
      changes.imageUrl = {
        label: 'Image URL',
        old: original.imageUrl || 'No image',
        new: current.imageUrl
      };
    }

    if (current.headline !== original.headline) {
      changes.headline = {
        label: 'Headline',
        old: original.headline || 'No headline',
        new: current.headline
      };
    }

    if (current.summary !== original.summary) {
      changes.summary = {
        label: 'Summary',
        old: original.summary || 'No summary',
        new: current.summary
      };
    }

    if (current.articleText !== original.articleText) {
      changes.articleText = {
        label: 'Article Text',
        old: original.articleText || 'No article text',
        new: current.articleText
      };
    }

    // Order is not meaningful on either side, so compare as sets - otherwise
    // deselecting and reselecting the same chip reads as a change
    const originalCats = original.categories || [];
    const currentCats = current.categories || [];
    const sameCategories =
      originalCats.length === currentCats.length &&
      originalCats.every((cat) => currentCats.includes(cat));
    if (!sameCategories) {
      changes.categories = {
        label: 'Categories',
        old: originalCats.join(', ') || 'None',
        new: currentCats.join(', ') || 'None'
      };
    }

    // Diffed on labels, which are the identity - two entries with the same
    // label and different cached titles are the same hashtag
    const originalTags = (original.hashtags || []).map((h) => h.label);
    const currentTags = (current.hashtags || []).map((h) => h.label);
    const sameHashtags =
      originalTags.length === currentTags.length &&
      originalTags.every((label) => currentTags.includes(label));
    if (!sameHashtags) {
      changes.hashtags = {
        label: 'Hashtags',
        old: originalTags.join(', ') || 'None',
        new: currentTags.join(', ') || 'None'
      };
    }

    return changes;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      imageUrl: '',
      headline: news.headline || '',
      summary: news.summary || '',
      categories: news.categories || [],
      hashtags: news.hashtags || [],
      articleText: news.articleText || '',
    });
  };

  const toggleCategory = (category) => {
    setEditForm({
      ...editForm,
      categories: editForm.categories.includes(category)
        ? editForm.categories.filter((c) => c !== category)
        : [...editForm.categories, category],
    });
  };

  const handleCancel = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true') {
      navigate(`/dashboard/news/${id}`, { replace: true });
      setIsEditing(false);
      setEditForm({ imageUrl: '', headline: '', summary: '', categories: [], hashtags: [], articleText: '' });
    } else {
      setIsEditing(false);
      setEditForm({ imageUrl: '', headline: '', summary: '', categories: [], hashtags: [], articleText: '' });
    }
  };

  const handleSave = async () => {
    const changes = getChanges();
    setCurrentChanges(changes);
    setEditReason('');
    setEditReasonError('');
    setShowChangesOverlay(true);
  };

  const confirmSave = async () => {
    // Validate editReason - minimum 5 characters
    if (!editReason || editReason.trim().length < 5) {
      setEditReasonError('Edit reason must be at least 5 characters');
      return;
    }

    setSaving(true);
    setEditReasonError('');
    setShowChangesOverlay(false);
    try {
      const response = await api.post(`/news/${id}/edit`, {
        headline: editForm.headline,
        summary: editForm.summary,
        imageUrl: editForm.imageUrl,
        categories: editForm.categories,
        hashtags: editForm.hashtags,
        editReason: editReason.trim(),
      });
      setNews(response.data.news);
      setEditHistory([response.data.editHistory, ...editHistory]);
      setIsEditing(false);
      setEditReason('');

      // The save can succeed here and still not have landed on Inshorts, or
      // have landed with a side effect the editor has to go and check. Those
      // are warnings, not successes - showing a green tick would be a lie.
      const { inshortsError, pushWarning, bottomPanelNotice } = response.data;
      setToast({
        message: inshortsError || pushWarning || bottomPanelNotice || response.message || 'News updated successfully',
        type: inshortsError || pushWarning ? 'error' : bottomPanelNotice ? 'warning' : 'success',
      });
    } catch (err) {
      setError('Failed to update news');
      setToast({ message: err.response?.data?.message || 'Failed to update news', type: 'error' });
      console.error('Error updating news:', err);
      setShowChangesOverlay(true); // Show overlay again on error
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (editData) => {
    setSaving(true);
    try {
      // Only send headline, summary, imageUrl - exclude categories
      const { headline, summary, imageUrl } = editData;
      const response = await api.post(`/news/${id}/edit`, {
        headline,
        summary,
        imageUrl,
      });
      setNews(response.data.news);
      setEditHistory([response.data.editHistory, ...editHistory]);
      setToast({ message: response.data.message || 'News updated successfully', type: 'success' });
    } catch (err) {
      setError('Failed to update news');
      setToast({ message: err.response?.data?.message || 'Failed to update news', type: 'error' });
      console.error('Error updating news:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/news/${id}`);
      navigate('/dashboard/news');
    } catch (err) {
      setError('Failed to delete news');
      console.error('Error deleting news:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleTimelineItem = (key) => {
    setExpandedTimelineItems(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(key)) {
        newExpanded.delete(key);
      } else {
        newExpanded.add(key);
      }
      return newExpanded;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      published: '#10b981',
      draft: '#f59e0b',
      in_review: '#60a5fa',
      archived: '#3b82f6',
      rejected: '#ef4444',
    };
    return colors[status?.toLowerCase()] || '#6b7280';
  };

  const formatStatus = (status) => {
    const statusMap = {
      draft: 'Draft',
      in_review: 'In Review',
      published: 'Published',
      archived: 'Archived',
      rejected: 'Rejected',
    };
    return statusMap[status?.toLowerCase()] || status || 'Draft';
  };

  // Check if user is admin or super admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  /**
   * Chips to offer in the category editor.
   *
   * Prefers the list the dashboard layout already fetched, and falls back to
   * the dummy-image keys when that comes back empty - which it does today,
   * since nothing populates the `categories` collection the endpoint reads.
   *
   * Whatever the news is already marked with is unioned in regardless, so an
   * existing marking can never become impossible to see or remove just because
   * it is missing from the list.
   */
  const categoryOptions = (() => {
    const fromContext = (categories || [])
      .filter((cat) => cat.id !== 'all')
      .map((cat) => cat.name)
      .filter(Boolean);
    const base = fromContext.length > 0 ? fromContext : Object.keys(CATEGORY_IMAGES);
    return [...new Set([...base, ...(editForm.categories || []), ...(news?.categories || [])])];
  })();

  // Copy image URL to clipboard
  const copyImageUrl = async () => {
    const urlToCopy = editForm.imageUrl || news?.imageUrl || '';
    if (urlToCopy) {
      try {
        await navigator.clipboard.writeText(urlToCopy);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Get display image (either edited URL or original news image)
  const getDisplayImage = () => {
    if (editForm.imageUrl) return editForm.imageUrl;
    return news?.imageUrl || '';
  };

  // Handle deck selection from NewsCard dropdown
  const handleDeckSelect = async (deckId) => {
    try {
      setCurrentDeckId(deckId);
      // Navigate to the deck detail page
      navigate(`/dashboard/decks/${deckId}`, { state: { selectedNewsId: news._id } });
    } catch (err) {
      console.error('Error selecting deck:', err);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await api.patch(`/news/${id}/status`, { status: newStatus });
      setNews(response.data.news);
    } catch (err) {
      setError(`Failed to change status to ${newStatus}`);
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  /**
   * Put a file or a cropped blob on the CDN and point the edit form at it.
   *
   * Shared by the file picker and the cropper: signed GCS upload first, then
   * the Inshorts media service for a CDN URL. A media-service failure is not
   * fatal - the GCS upload already succeeded, so we keep that URL rather than
   * throwing away the editor's work.
   */
  const uploadBlobAndRegister = async (blob, fileName) => {
    setUploadingImage(true);
    setUploadProgress(0);

    try {
      const contentType = blob.type || 'image/jpeg';
      const signedResponse = await newsService.getSignedUploadUrl({
        fileName,
        contentType,
        fileSize: blob.size,
      });

      const { signedUrl, publicUrl } = signedResponse.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        };

        xhr.onerror = () => reject(new Error('Upload failed'));

        xhr.send(blob);
      });

      let finalUrl = publicUrl;
      try {
        const mediaResponse = await newsService.uploadImageFromUrl(publicUrl);
        finalUrl = mediaResponse.data.publicUrl;
      } catch (mediaErr) {
        console.error('Media service upload failed, using storage URL:', mediaErr);
      }

      handleInputChange('imageUrl', finalUrl);
      return finalUrl;
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  // Handle image upload to GCP
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires onChange
    e.target.value = '';
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!validTypes.includes(file.type)) {
      setToast({ message: 'Only JPEG, PNG, WebP, GIF, and AVIF images are allowed', type: 'error' });
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({ message: 'File size exceeds 5MB limit', type: 'error' });
      return;
    }

    try {
      await uploadBlobAndRegister(file, file.name);
      setToast({ message: 'Image uploaded successfully', type: 'success' });
    } catch (err) {
      console.error('Error uploading image:', err);
      setToast({ message: 'Failed to upload image', type: 'error' });
    }
  };

  /**
   * Load an image the editor pasted the address of.
   *
   * Goes through the media service, which fetches the URL itself - so this
   * works for addresses our own network cannot reach, and the editor ends up
   * with a CDN URL rather than a hotlink to someone else's server.
   */
  const handleLoadImageUrl = async () => {
    const candidate = imageUrlInput.trim();
    if (!candidate) return;

    let parsed;
    try {
      parsed = new URL(candidate);
    } catch {
      setToast({ message: 'That does not look like a valid URL', type: 'error' });
      return;
    }
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      setToast({ message: 'Image URL must start with http:// or https://', type: 'error' });
      return;
    }

    setLoadingUrlImage(true);
    try {
      const response = await newsService.uploadImageFromUrl(parsed.href);
      handleInputChange('imageUrl', response.data.publicUrl);
      setImageUrlInput('');
      setToast({ message: 'Image loaded. Crop it if the framing needs work.', type: 'success' });
    } catch (err) {
      console.error('Error loading image from URL:', err);
      setToast({
        message: err.response?.data?.message || 'Could not load an image from that URL',
        type: 'error',
      });
    } finally {
      setLoadingUrlImage(false);
    }
  };

  // Open the cropper on whatever image the form is currently showing
  const handleOpenCropper = () => {
    const current = getDisplayImage();
    if (!current) {
      setToast({ message: 'There is no image to crop yet', type: 'error' });
      return;
    }
    setCropSource(current);
  };

  const handleApplyCrop = async (blob) => {
    try {
      await uploadBlobAndRegister(blob, `crop-${Date.now()}.jpg`);
      setCropSource(null);
      setToast({ message: 'Crop applied', type: 'success' });
    } catch (err) {
      console.error('Error uploading cropped image:', err);
      setToast({ message: 'Failed to upload the cropped image', type: 'error' });
    }
  };

  // Custom tooltip handlers
  const showTooltip = (e, text, type) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      type
    });
  };

  const hideTooltip = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'matched' });
  };

  // Copy Inshorts URL to clipboard
  const copyInshortsUrl = async (url) => {
    if (url) {
      try {
        await navigator.clipboard.writeText(url);
        setCopiedInshortsUrl(true);
        setTimeout(() => setCopiedInshortsUrl(false), 2000);
      } catch (err) {
        console.error('Failed to copy URL:', err);
      }
    }
  };

  // Copy Inshorts API log text (curl / response) to clipboard
  const copyLogText = async (text, logKey) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLogId(logKey);
      setTimeout(() => setCopiedLogId(null), 2000);
    } catch (err) {
      console.error('Failed to copy log text:', err);
    }
  };

  const getLogMethodColor = (method) => {
    const colors = {
      GET: '#10b981',
      POST: '#3b82f6',
      PUT: '#f59e0b',
      DELETE: '#ef4444',
      PATCH: '#8b5cf6',
    };
    return colors[method] || '#6b7280';
  };

  const formatLogTimestamp = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'dd/MM/yy HH:mm:ss');
    } catch {
      return '';
    }
  };

  // Auto-resize textareas when editing starts or content changes
  useEffect(() => {
    if (isEditing) {
      // Update height for both textareas
      const updateHeight = () => {
        if (headlineRef.current) {
          const avgCharsPerLine = 80;
          const lineHeight = 22;
          const contentLines = Math.ceil(editForm.headline.length / avgCharsPerLine);
          const newlineLines = editForm.headline.split('\n').length - 1;
          const totalLines = Math.max(1, contentLines + newlineLines);
          headlineRef.current.style.height = Math.max(50, totalLines * lineHeight) + 'px';
        }
        if (summaryRef.current) {
          const avgCharsPerLine = 80;
          const lineHeight = 22;
          const contentLines = Math.ceil(editForm.summary.length / avgCharsPerLine);
          const newlineLines = editForm.summary.split('\n').length - 1;
          const totalLines = Math.max(1, contentLines + newlineLines);
          summaryRef.current.style.height = Math.max(176, totalLines * lineHeight) + 'px';
        }
      };

      // Use requestAnimationFrame for immediate sizing
      requestAnimationFrame(updateHeight);
      const timer1 = setTimeout(updateHeight, 100);
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [isEditing, editForm.headline, editForm.summary]);

  // Fetch raw Inshorts data by hash ID (only when expanded)
  useEffect(() => {
    const fetchRawInshortsData = async () => {
      if (!news?.hashId || !isDeveloperSectionExpanded) return;

      setLoadingRawData(true);
      try {
        const response = await api.get('/news/by-hash', {
          params: { 'hash-id': news.hashId }
        });
        setRawInshortsData(response.data.news);
      } catch (err) {
        console.error('Error fetching raw Inshorts data:', err);
      } finally {
        setLoadingRawData(false);
      }
    };

    fetchRawInshortsData();
  }, [news?.hashId, isDeveloperSectionExpanded]);

  // Fetch Inshorts API logs for this news (only when expanded)
  // Send the news id alongside the hash: the create_post call carries no hash
  // in its curl request, and a failed create has no hash at all.
  useEffect(() => {
    const fetchInshortsLogs = async () => {
      if ((!news?.hashId && !news?._id) || !isInshortsLogsExpanded) return;

      setLoadingInshortsLogs(true);
      try {
        const response = await devService.getApiLogs({
          ...(news.hashId ? { hashId: news.hashId } : {}),
          ...(news._id ? { newsId: news._id } : {}),
        });
        const logs = response.logs || [];
        setInshortsLogs(logs);
        setExpandedLogId(logs.length > 0 ? logs[0].id : null);
      } catch (err) {
        console.error('Error fetching Inshorts API logs:', err);
      } finally {
        setLoadingInshortsLogs(false);
      }
    };

    fetchInshortsLogs();
  }, [news?.hashId, news?._id, isInshortsLogsExpanded]);

  if (loading || !news) {
    return (
      <div className="news-detail-page">
        <div className="news-detail-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="news-detail-page">
        <div className={`news-detail-container ${isEditing ? 'news-detail-container--editing' : ''}`}>
          {/* Action Buttons Bar */}
          <div className="news-detail-actions-bar">
            <div className="news-detail-actions-right">
              {/* Status Change Button - Send for Review / Publish */}
              {!isEditing && (
                <>
                  {/* {news.status === 'draft' && (
                    <button
                      onClick={() => handleStatusChange('in_review')}
                      disabled={updatingStatus}
                      className="btn-send-review"
                      title="Send for Review"
                    >
                      {updatingStatus ? 'Sending...' : 'Send for Review'}
                    </button>
                  )} */}
                  {news.status === 'in_review' && isAdmin && (
                    <button
                      onClick={() => handleStatusChange('published')}
                      disabled={updatingStatus}
                      className="btn-publish"
                      title="Publish"
                    >
                      {updatingStatus ? 'Publishing...' : 'Publish'}
                    </button>
                  )}
                </>
              )}
              {isEditing && (
                <button onClick={handleCancel} className="btn-cancel-edit" title="Cancel">
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Main Content - Two Column Layout */}
          {isEditing ? (
            <div className="news-detail-edit-layout">
              {/* Left Part (2/3): Image/Category and Headline/Summary */}
              <div className="news-edit-left-part">
                {/* Header: Update Shorts */}
                <div className="news-edit-main-header">
                  <h3>Update Shorts</h3>
                </div>

                {/* Section 1: Image and Categories */}
                <div className="news-edit-section no-border">
                  <div className="edit-section-content">
                    <label>Image</label>
                    <div className="image-preview">
                      <img src={getDisplayImage()} alt="Preview" />
                      <div className="image-preview-actions">
                        <button
                          className="copy-url-btn"
                          onClick={copyImageUrl}
                          title="Copy Image URL"
                        >
                          {copiedUrl ? 'Link copied' : <Copy size={14} />}
                        </button>
                        <input
                          type="file"
                          id="image-upload-input"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          style={{ display: 'none' }}
                        />
                        <button
                          className="crop-image-btn"
                          onClick={handleOpenCropper}
                          disabled={uploadingImage || !getDisplayImage()}
                          title="Crop image"
                        >
                          <Crop size={16} />
                        </button>
                        <button
                          className={`image-upload-btn${uploadingImage ? ' uploading' : ''}`}
                          onClick={() => document.getElementById('image-upload-input').click()}
                          disabled={uploadingImage}
                          title={uploadingImage ? `Uploading ${uploadProgress}%` : 'Upload Image'}
                          style={uploadingImage ? { '--upload-progress': `${uploadProgress}%` } : { '--upload-progress': '0%' }}
                        >
                          {uploadingImage ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Upload size={16} />
                          )}
                        </button>
                        {/* No remove action by design: a short always keeps an
                            image. The editor can replace it - upload, paste a
                            URL, or crop - but never clear it. */}
                      </div>
                    </div>

                    {/* Paste an image address instead of picking a file */}
                    <div className="image-url-row">
                      <input
                        type="url"
                        className="image-url-input"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleLoadImageUrl();
                          }
                        }}
                        placeholder="Enter Image URL to update Image"
                        disabled={loadingUrlImage || uploadingImage}
                      />
                      <button
                        type="button"
                        className="image-url-load-btn"
                        onClick={handleLoadImageUrl}
                        disabled={!imageUrlInput.trim() || loadingUrlImage || uploadingImage}
                      >
                        {loadingUrlImage ? <Loader2 size={14} className="animate-spin" /> : 'Fetch'}
                      </button>
                    </div>

                    <label>Categories</label>
                    <div className="categories-chips">
                      {categoryOptions.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          className={`category-chip ${editForm.categories.includes(cat) ? 'selected' : ''}`}
                          onClick={() => toggleCategory(cat)}
                        >
                          <span>{cat}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Vertical Divider Line */}
                <div className="edit-section-divider-vertical"></div>

                {/* Section 2: Headline, Summary and Hashtags */}
                <div className="news-edit-section no-border">
                  <div className="edit-section-content">
                    <label>
                      Headline
                      <span className="word-count">{countWords(editForm.headline)} words, {editForm.headline.length} characters</span>
                    </label>
                    <textarea
                      id="headline-textarea"
                      ref={headlineRef}
                      value={editForm.headline}
                      rows={Math.max(2, Math.ceil(editForm.headline.length / 80) + editForm.headline.split('\n').length)}
                      style={{
                        height: Math.max(50, (Math.ceil(editForm.headline.length / 80) + editForm.headline.split('\n').length) * 22) + 'px'
                      }}
                      onChange={(e) => {
                        handleInputChange('headline', e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                      placeholder="Enter headline..."
                      className="edit-textarea auto-resize-textarea"
                    />

                    <label>
                      Summary
                      <span className="word-count">{countWords(editForm.summary)} words, {editForm.summary.length} characters</span>
                    </label>
                    <textarea
                      id="summary-textarea"
                      ref={summaryRef}
                      value={editForm.summary}
                      rows={Math.max(8, Math.ceil(editForm.summary.length / 80) + editForm.summary.split('\n').length)}
                      style={{
                        height: Math.max(176, (Math.ceil(editForm.summary.length / 80) + editForm.summary.split('\n').length) * 22) + 'px'
                      }}
                      onChange={(e) => {
                        handleInputChange('summary', e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                      placeholder="Enter summary (60 words recommended)..."
                      className="edit-textarea auto-resize-textarea"
                    />

                    {/* The label is the picker's own, so the selected hashtag
                        can sit on that row beside it */}
                    <HashtagPicker
                      label="Hashtag"
                      value={editForm.hashtags}
                      onChange={(hashtags) => handleInputChange('hashtags', hashtags)}
                      language={news.metadata?.language || DEFAULT_LANGUAGE}
                    />
                    {/* Inshorts clears the short's bottom panel whenever
                        hashtags are written, so warn before the editor saves
                        rather than after */}
                    {getChanges().hashtags && (
                      <div className="hashtag-side-effect-note">
                        Changing hashtags resets this short's bottom panel on Inshorts — check it after saving.
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Changes Button */}
                <div className="edit-save-button-container">
                  <button
                    onClick={handleSave}
                    className="btn-save-changes"
                    disabled={saving || Object.keys(getChanges()).length === 0}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                {/* Changes Overlay on Left Part */}
                {showChangesOverlay && currentChanges && (
                  <div className="changes-overlay-left-part">
                  <div className="changes-overlay-content">
                    <div className="changes-overlay-header">
                      <h3>Changes to be Saved</h3>
                      <button
                        onClick={() => setShowChangesOverlay(false)}
                        className="btn-close-overlay"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <div className="changes-overlay-body">
                      {/* Edit Reason Input Field */}
                      {Object.keys(currentChanges).length > 0 && (
                        <div className="edit-reason-section">
                          <label htmlFor="edit-reason-input">
                            Edit Reason <span className="required">*</span>
                          </label>
                          <textarea
                            id="edit-reason-input"
                            value={editReason}
                            onChange={(e) => {
                              setEditReason(e.target.value);
                              setEditReasonError('');
                            }}
                            placeholder="Please provide a reason for this edit (min. 5 characters)..."
                            className={`edit-reason-textarea ${editReasonError ? 'edit-reason-error' : ''}`}
                            rows={2}
                          />
                          {editReasonError && (
                            <div className="edit-reason-error-message">{editReasonError}</div>
                          )}
                        </div>
                      )}
                      {Object.keys(currentChanges).length === 0 ? (
                        <div className="no-changes-overlay">
                          <p>No changes have been made.</p>
                          <button
                            onClick={() => setShowChangesOverlay(false)}
                            className="btn-overlay-close"
                          >
                            Close
                          </button>
                        </div>
                      ) : (
                        <>
                          {currentChanges.imageUrl ? (
                            // With Image Change: Split layout
                            <div className="changes-split-layout">
                              {/* Left Side: Images */}
                              <div className="changes-left-side">
                                <div className="change-image-section">
                                  <div className="change-image-preview change-image-before">
                                    <div className="change-image-label before-label">Before:</div>
                                    {currentChanges.imageUrl.old && currentChanges.imageUrl.old !== 'No image' ? (
                                      <img src={currentChanges.imageUrl.old} alt="Before" />
                                    ) : (
                                      <div className="change-image-placeholder">No image</div>
                                    )}
                                  </div>
                                  <div className="change-image-preview change-image-after">
                                    <div className="change-image-label after-label">After:</div>
                                    {currentChanges.imageUrl.new && currentChanges.imageUrl.new !== 'No image' ? (
                                      <img src={currentChanges.imageUrl.new} alt="After" />
                                    ) : (
                                      <div className="change-image-placeholder">No image</div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Vertical Divider */}
                              <div className="changes-vertical-divider"></div>

                              {/* Right Side: Other Changes */}
                              <div className="changes-right-side">
                                <div className="changes-list-compact">
                                  {/* Headline Changes */}
                                  {currentChanges.headline && (
                                    <div className="change-item-compact">
                                      <div className="change-item-label">Headline</div>
                                      <div className="change-item-text-diff-compact">
                                        {getWordDiff(currentChanges.headline.old, currentChanges.headline.new).map((word, i) => (
                                          <span key={i} className={`diff-word diff-${word.type}`}>
                                            {word.text}{' '}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Summary Changes */}
                                  {currentChanges.summary && (
                                    <div className="change-item-compact">
                                      <div className="change-item-label">Summary</div>
                                      <div className="change-item-text-diff-compact">
                                        {getWordDiff(currentChanges.summary.old, currentChanges.summary.new).map((word, i) => (
                                          <span key={i} className={`diff-word diff-${word.type}`}>
                                            {word.text}{' '}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Article Text Changes */}
                                  {currentChanges.articleText && (
                                    <div className="change-item-compact">
                                      <div className="change-item-label">Article Text</div>
                                      <div className="change-item-text-diff-compact">
                                        {getWordDiff(currentChanges.articleText.old, currentChanges.articleText.new).slice(0, 100).map((word, i) => (
                                          <span key={i} className={`diff-word diff-${word.type}`}>
                                            {word.text}{' '}
                                          </span>
                                        ))}
                                        {getWordDiff(currentChanges.articleText.old, currentChanges.articleText.new).length > 100 && (
                                          <span className="diff-word diff-same">...</span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Category Changes - a chip list, so show it
                                      whole rather than word-diffed */}
                                  {currentChanges.categories && (
                                    <div className="change-item-compact">
                                      <div className="change-item-label">Categories</div>
                                      <div className="change-item-plain-diff">
                                        <span className="diff-word diff-removed">{currentChanges.categories.old}</span>
                                        <span className="change-item-arrow">→</span>
                                        <span className="diff-word diff-added">{currentChanges.categories.new}</span>
                                      </div>
                                    </div>
                                  )}

                                  {currentChanges.hashtags && (
                                    <div className="change-item-compact">
                                      <div className="change-item-label">Hashtags</div>
                                      <div className="change-item-plain-diff">
                                        <span className="diff-word diff-removed">{currentChanges.hashtags.old}</span>
                                        <span className="change-item-arrow">→</span>
                                        <span className="diff-word diff-added">{currentChanges.hashtags.new}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // No Image Change: Vertical list layout
                            <div className="changes-vertical-list">
                              {/* Headline Changes */}
                              {currentChanges.headline && (
                                <div className="change-item-vertical">
                                  <div className="change-item-label">Headline</div>
                                  <div className="change-item-text-diff">
                                    {getWordDiff(currentChanges.headline.old, currentChanges.headline.new).map((word, i) => (
                                      <span key={i} className={`diff-word diff-${word.type}`}>
                                        {word.text}{' '}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Summary Changes */}
                              {currentChanges.summary && (
                                <div className="change-item-vertical">
                                  <div className="change-item-label">Summary</div>
                                  <div className="change-item-text-diff">
                                    {getWordDiff(currentChanges.summary.old, currentChanges.summary.new).map((word, i) => (
                                      <span key={i} className={`diff-word diff-${word.type}`}>
                                        {word.text}{' '}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Article Text Changes */}
                              {currentChanges.articleText && (
                                <div className="change-item-vertical">
                                  <div className="change-item-label">Article Text</div>
                                  <div className="change-item-text-diff">
                                    {getWordDiff(currentChanges.articleText.old, currentChanges.articleText.new).slice(0, 200).map((word, i) => (
                                      <span key={i} className={`diff-word diff-${word.type}`}>
                                        {word.text}{' '}
                                      </span>
                                    ))}
                                    {getWordDiff(currentChanges.articleText.old, currentChanges.articleText.new).length > 200 && (
                                      <span className="diff-word diff-same">...</span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Category Changes - a chip list, so show it
                                  whole rather than word-diffed */}
                              {currentChanges.categories && (
                                <div className="change-item-vertical">
                                  <div className="change-item-label">Categories</div>
                                  <div className="change-item-plain-diff">
                                    <span className="diff-word diff-removed">{currentChanges.categories.old}</span>
                                    <span className="change-item-arrow">→</span>
                                    <span className="diff-word diff-added">{currentChanges.categories.new}</span>
                                  </div>
                                </div>
                              )}

                              {currentChanges.hashtags && (
                                <div className="change-item-vertical">
                                  <div className="change-item-label">Hashtags</div>
                                  <div className="change-item-plain-diff">
                                    <span className="diff-word diff-removed">{currentChanges.hashtags.old}</span>
                                    <span className="change-item-arrow">→</span>
                                    <span className="diff-word diff-added">{currentChanges.hashtags.new}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {Object.keys(currentChanges).length > 0 && (
                      <div className="changes-overlay-footer">
                        <button
                          onClick={() => setShowChangesOverlay(false)}
                          className="btn-overlay-cancel"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmSave}
                          disabled={saving || !editReason || editReason.trim().length < 5}
                          className="btn-overlay-confirm"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>

              {/* Right Part (1/3): Full Article */}
              <div className="news-edit-right-part">
                {/* Header: Full Article */}
                <div className="news-edit-right-header">
                  <h3>Full Article</h3>
                  {news.sourceUrl && (
                    <a
                      href={news.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="full-article-link"
                      title="Open full article in new tab"
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
                {/* Section 3: Full Article */}
                <div className="news-edit-section news-edit-section--full-article">
                  <div className="edit-section-content">
                    <div className="full-article-display">
                      {(editForm.articleText || news.articleText || '')
                        .replace(/\n\s*\n\s*\n+/g, '\n\n')
                        .replace(/[ \t]+/g, ' ')
                        .trim()}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          ) : (
            <div className="news-detail-main-content">
              {/* Left Column - News Card */}
              <div className="news-detail-left-column">
                <NewsCard
                  news={news}
                  onEdit={(id, data) => handleSaveEdit(data)}
                  showEdit={false}
                  showAddToDeckButton={false}
                  showEditShortsButton={true}
                  onEnterEditMode={handleEdit}
                  onClick={() => {}}
                  newsDecks={newsDecks}
                  currentDeckId={currentDeckId}
                  onDeckSelect={handleDeckSelect}
                />
              </div>

              {/* Middle Column - Full Article */}
              <div className="news-detail-middle-column">
                {news.articleText && (
                  <div className="news-detail-article-section">
                    <div className="news-section-label">
                      Full Article
                      {news.sourceUrl && (
                        <a
                          href={news.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="full-article-link"
                          title="Open full article in new tab"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    <div className="news-article-text">
                      {news.articleText.trim().replace(/\n\s*\n\s*\n+/g, '\n\n')}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - History */}
              <div className="news-detail-history-column">
                <div className="news-detail-history-header">
                  <h2>
                    <FileText size={14} />
                    <span>History</span>
                  </h2>
                  <span className="news-history-count">{editHistory.length}</span>
                </div>
                <div className="news-history-list">
                  {editHistory.length === 0 ? (
                    <div className="news-history-empty">
                      <FileText size={32} />
                      <p>No edit history available</p>
                    </div>
                  ) : (
                    editHistory.map((edit, index) => {
                      const itemKey = edit._id || `${edit.editedAt}-${index}`;
                      const isDeckOperation = edit.actionType === 'added_to_deck' || edit.actionType === 'removed_from_deck';
                      const isLiveOperation = edit.actionType === 'made_live';
                      const isUnpublishOperation = edit.actionType === 'unpublished';

                      return (
                        <div key={itemKey} className="news-history-item">
                          <div
                            className={`news-history-item-header ${isDeckOperation ? 'news-history-item-header--deck' : ''} ${isLiveOperation ? 'news-history-item-header--live' : ''} ${isUnpublishOperation ? 'news-history-item-header--unpublished' : ''}`}
                            onClick={() => toggleTimelineItem(itemKey)}
                          >
                            <div className={`news-history-dot ${isDeckOperation ? 'news-history-dot--deck' : ''} ${isLiveOperation ? 'news-history-dot--live' : ''} ${isUnpublishOperation ? 'news-history-dot--unpublished' : ''}`}></div>
                            <div className="news-history-info">
                              {isDeckOperation ? (
                                <div className="news-history-deck-operation">
                                  {edit.actionType === 'added_to_deck' ? 'Added to deck' : 'Removed from deck'} (
                                  <span className="deck-name">
                                    {edit.deckData?.deckName || edit.deckData?.deckId?.name || 'Unknown Deck'}
                                  </span>
                                  ) by <span className="user-email">{edit.editedBy}</span>
                                </div>
                              ) : isLiveOperation ? (
                                <div className="news-history-live-operation">
                                  <span className="live-operation-text">Made Live</span>
                                  <span className="live-operation-details">
                                    {edit.liveData?.feed === 'national' ? '(National)' : '(Regional)'}
                                    {edit.liveData?.location && (
                                      <span className="live-location">
                                        {edit.liveData.location.locationLevel === 'ALL' && ' - All India'}
                                        {edit.liveData.location.locationLevel === 'STATE' && edit.liveData.location.state && ` - ${edit.liveData.location.state}`}
                                        {edit.liveData.location.locationLevel === 'DISTRICT' && edit.liveData.location.district && ` - ${edit.liveData.location.district}`}
                                        {edit.liveData.location.locationLevel === 'SUB_DISTRICT' && edit.liveData.location.tehsil && ` - ${edit.liveData.location.tehsil}`}
                                      </span>
                                    )}
                                  </span>
                                   by <span className="user-email">{edit.editedBy}</span>
                                </div>
                              ) : isUnpublishOperation ? (
                                <div className="news-history-unpublish-operation">
                                  <span className="unpublish-operation-text">Unpublished</span>
                                  {edit.liveData?.location?.locationLevel && (
                                    <span className="unpublish-operation-details">
                                      {edit.liveData.location.locationLevel === 'ALL'
                                        ? ' (was All India)'
                                        : ` (was ${[
                                            edit.liveData.location.state,
                                            edit.liveData.location.district,
                                            edit.liveData.location.tehsil,
                                          ].filter(Boolean).join(' • ')})`}
                                    </span>
                                  )}
                                  {' '}by <span className="user-email">{edit.editedBy}</span>
                                </div>
                              ) : (
                                <div className="news-history-edited-by">
                                  Edited by {edit.editedBy}
                                </div>
                              )}
                              <div className="news-history-edited-at">
                                {formatDate(edit.editedAt)}
                              </div>
                            </div>
                            <div className="news-history-toggle">
                              {expandedTimelineItems.has(itemKey) ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </div>
                          </div>

                          {expandedTimelineItems.has(itemKey) && edit.changes && Object.keys(edit.changes).length > 0 && (
                            <div className="news-history-content">
                              <div className="news-history-changes">
                                <div className="news-history-changes-label">Changes Made:</div>
                                <div className="news-history-changes-list">
                                  {Object.entries(edit.changes).map(([key, value]) => {
                                    // For image changes, show thumbnails instead of text diff
                                    if (key === 'imageUrl') {
                                      const [oldValue, newValue] = value.split(' → ');
                                      return (
                                        <div key={key} className="news-history-change-item news-history-image-change">
                                          <span className="news-history-change-label">{key}:</span>
                                          <div className="news-history-image-comparison">
                                            <div className="history-image-side before-side">
                                              <span className="history-image-label">Before:</span>
                                              {oldValue && oldValue !== 'No image' ? (
                                                <img src={oldValue} alt="Before" className="history-image-thumb" />
                                              ) : (
                                                <div className="history-image-placeholder">No image</div>
                                              )}
                                            </div>
                                            <div className="history-image-side after-side">
                                              <span className="history-image-label">After:</span>
                                              {newValue && newValue !== 'No image' ? (
                                                <img src={newValue} alt="After" className="history-image-thumb" />
                                              ) : (
                                                <div className="history-image-placeholder">No image</div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    const [oldValue, newValue] = value.split(' → ');
                                    const diff = getWordDiff(oldValue, newValue);
                                    return (
                                      <div key={key} className="news-history-change-item">
                                        <span className="news-history-change-label">{key}:</span>
                                        <div className="news-history-change-diff">
                                          {diff.map((word, i) => (
                                            <span
                                              key={i}
                                              className={`diff-word diff-${word.type}`}
                                            >
                                              {word.text}{' '}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Horizontal Partition */}
        <div className="news-detail-horizontal-partition"></div>

        {/* Developer Options - side by side */}
        <div className="news-detail-developer-row">
        {/* Developer Options - Part 1: Fetch this news on Inshorts */}
        <div className="news-detail-developer-section">
          <div
            className="developer-section-header"
            onClick={() => setIsDeveloperSectionExpanded(!isDeveloperSectionExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <h3>Developer Options - Fetch this news on Inshorts</h3>
            <div className="developer-section-toggle">
              {isDeveloperSectionExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>

          {isDeveloperSectionExpanded && (
          <div className="developer-section-content">
            {loadingRawData ? (
              <div className="developer-section-loading">
                <Loader />
              </div>
            ) : rawInshortsData ? (
              <>
                {/* Highlighted Fields Box - News Card Style */}
                <div className="developer-highlighted-fields">
                  <div className="developer-news-card-container">
                    {rawInshortsData.default_news ? (
                      <div className="developer-news-card">
                        {/* Section 1: Image with Category Overlay and Match Indicator */}
                        {rawInshortsData.default_news.image_url && (
                          <div className="developer-news-card-image">
                            <img src={rawInshortsData.default_news.image_url} alt="News" />
                            {/* Image Match Indicator */}
                            <div
                              className="developer-match-indicator image-match-indicator"
                              style={{
                                background: news?.imageUrl === rawInshortsData.default_news.image_url ? '#10b981' : '#ef4444',
                                borderColor: news?.imageUrl === rawInshortsData.default_news.image_url ? '#059669' : '#dc2626'
                              }}
                              onMouseEnter={(e) => showTooltip(e, news?.imageUrl === rawInshortsData.default_news.image_url ? 'matched from Easy Panel' : 'not matched from Easy Panel', news?.imageUrl === rawInshortsData.default_news.image_url ? 'matched' : 'not-matched')}
                              onMouseLeave={hideTooltip}
                            >
                              {news?.imageUrl === rawInshortsData.default_news.image_url ? '✓' : '✗'}
                            </div>
                            {rawInshortsData.default_news.categories && (
                              <div className="developer-news-card-category-overlay">
                                <span className="developer-category-overlay-tag">
                                  {Array.isArray(rawInshortsData.default_news.categories) ?
                                    rawInshortsData.default_news.categories.join(', ') :
                                    rawInshortsData.default_news.categories}
                                </span>
                                {/* Categories Match Indicator */}
                                <div
                                  className="developer-match-indicator categories-match-indicator"
                                  style={{
                                    background: (() => {
                                      const newsCats = news?.categories?.map(c => c.toLowerCase()) || [];
                                      const rawCats = rawInshortsData.default_news.categories?.map(c => c.toLowerCase()) || [];
                                      const matched = newsCats.length === rawCats.length &&
                                        newsCats.every(cat => rawCats.includes(cat));
                                      return matched ? '#10b981' : '#ef4444';
                                    })(),
                                    borderColor: (() => {
                                      const newsCats = news?.categories?.map(c => c.toLowerCase()) || [];
                                      const rawCats = rawInshortsData.default_news.categories?.map(c => c.toLowerCase()) || [];
                                      const matched = newsCats.length === rawCats.length &&
                                        newsCats.every(cat => rawCats.includes(cat));
                                      return matched ? '#059669' : '#dc2626';
                                    })()
                                  }}
                                  onMouseEnter={(e) => {
                                    const newsCats = news?.categories?.map(c => c.toLowerCase()) || [];
                                    const rawCats = rawInshortsData.default_news.categories?.map(c => c.toLowerCase()) || [];
                                    const matched = newsCats.length === rawCats.length &&
                                      newsCats.every(cat => rawCats.includes(cat));
                                    showTooltip(e, matched ? 'matched from Easy Panel' : 'not matched from Easy Panel', matched ? 'matched' : 'not-matched');
                                  }}
                                  onMouseLeave={hideTooltip}
                                >
                                  {(() => {
                                    const newsCats = news?.categories?.map(c => c.toLowerCase()) || [];
                                    const rawCats = rawInshortsData.default_news.categories?.map(c => c.toLowerCase()) || [];
                                    const matched = newsCats.length === rawCats.length &&
                                      newsCats.every(cat => rawCats.includes(cat));
                                    return matched ? '✓' : '✗';
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Section 2: Title with Match Indicator */}
                        {rawInshortsData.default_news.title && (
                          <div className="developer-news-card-section developer-card-section-with-indicator">
                            <div className="developer-news-card-title">
                              {rawInshortsData.default_news.title}
                            </div>
                            {/* Title Match Indicator */}
                            <div
                              className="developer-match-indicator title-match-indicator"
                              style={{
                                background: news?.headline === rawInshortsData.default_news.title ? '#10b981' : '#ef4444',
                                borderColor: news?.headline === rawInshortsData.default_news.title ? '#059669' : '#dc2626'
                              }}
                              onMouseEnter={(e) => showTooltip(e, news?.headline === rawInshortsData.default_news.title ? 'matched from Easy Panel' : 'not matched from Easy Panel', news?.headline === rawInshortsData.default_news.title ? 'matched' : 'not-matched')}
                              onMouseLeave={hideTooltip}
                            >
                              {news?.headline === rawInshortsData.default_news.title ? '✓' : '✗'}
                            </div>
                          </div>
                        )}

                        {/* Section 3: Summary with Match Indicator */}
                        {rawInshortsData.default_news.content && (
                          <div className="developer-news-card-section developer-card-section-with-indicator">
                            <div className="developer-news-card-text">
                              {rawInshortsData.default_news.content}
                            </div>
                            {/* Summary Match Indicator */}
                            <div
                              className="developer-match-indicator summary-match-indicator"
                              style={{
                                background: news?.summary === rawInshortsData.default_news.content ? '#10b981' : '#ef4444',
                                borderColor: news?.summary === rawInshortsData.default_news.content ? '#059669' : '#dc2626'
                              }}
                              onMouseEnter={(e) => showTooltip(e, news?.summary === rawInshortsData.default_news.content ? 'matched from Easy Panel' : 'not matched from Easy Panel', news?.summary === rawInshortsData.default_news.content ? 'matched' : 'not-matched')}
                              onMouseLeave={hideTooltip}
                            >
                              {news?.summary === rawInshortsData.default_news.content ? '✓' : '✗'}
                            </div>
                          </div>
                        )}

                        {/* Section 4: Metadata */}
                        <div className="developer-news-card-section">
                          <div className="developer-news-card-metadata">
                            {(rawInshortsData.default_news.not_relevant_india !== undefined || rawInshortsData.default_news.news_location_publish_enum) && (
                              <div className="metadata-row-two-items">
                                {rawInshortsData.default_news.not_relevant_india !== undefined && (
                                  <div className="metadata-item-inline">
                                    <span className="metadata-label">Not Relevant India:</span>
                                    <span className="metadata-value">{String(rawInshortsData.default_news.not_relevant_india)}</span>
                                  </div>
                                )}
                                {rawInshortsData.default_news.news_location_publish_enum && (
                                  <div className="metadata-item-inline">
                                    <span className="metadata-label">Location Enum:</span>
                                    <span className="metadata-value">
                                      {rawInshortsData.default_news.news_location_publish_enum}
                                      {(() => {
                                        const locationEnum = rawInshortsData.default_news.news_location_publish_enum;
                                        if (locationEnum === 'ALL') {
                                          return ' (India)';
                                        } else if (locationEnum === 'STATE' && rawInshortsData.default_news.news_state) {
                                          return ` (${rawInshortsData.default_news.news_state})`;
                                        } else if (locationEnum === 'DISTRICT' && rawInshortsData.default_news.news_district) {
                                          return ` (${rawInshortsData.default_news.news_state}, ${rawInshortsData.default_news.news_district})`;
                                        } else if (locationEnum === 'SUB_DISTRICT' && rawInshortsData.default_news.news_sub_district) {
                                          return ` (${rawInshortsData.default_news.news_state}, ${rawInshortsData.default_news.news_district}, ${rawInshortsData.default_news.news_sub_district})`;
                                        }
                                        return '';
                                      })()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {rawInshortsData.default_news.shortened_url && String(rawInshortsData.default_news.shortened_url).trim() !== '' && (
                              <div className="metadata-item metadata-item-full">
                                <span className="metadata-label">Inshorts URL:</span>
                                <div className="metadata-url-with-copy">
                                  <a href={rawInshortsData.default_news.shortened_url} target="_blank" rel="noopener noreferrer" className="developer-link">
                                    {rawInshortsData.default_news.shortened_url}
                                  </a>
                                  <button
                                    className="url-copy-button"
                                    onClick={() => copyInshortsUrl(rawInshortsData.default_news.shortened_url)}
                                    title={copiedInshortsUrl ? 'Copied!' : 'Copy URL'}
                                  >
                                    {copiedInshortsUrl ? <span className="copy-copied-text">Copied</span> : <Copy size={12} />}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="developer-news-card-empty">
                        <p>No default_news data available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* JSON Block for remaining data */}
                <div className="developer-json-block">
                  <div className="developer-json-header">
                    <h4>Complete Raw Data (JSON)</h4>
                  </div>
                  <pre className="developer-json-content">
                    {JSON.stringify(rawInshortsData, null, 2)}
                  </pre>
                </div>
              </>
            ) : news?.hashId ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                <p>No raw data available for this news</p>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                <p>No hash ID available for this news</p>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Developer Options - Part 2: Inshorts API Logs for this news */}
        {isAdmin && (
        <div className="news-detail-developer-section">
          <div
            className="developer-section-header"
            onClick={() => setIsInshortsLogsExpanded(!isInshortsLogsExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <h3>Developer Options - Inshorts API Logs</h3>
            <div className="developer-section-toggle">
              {isInshortsLogsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>

          {isInshortsLogsExpanded && (
            <div className="inshorts-logs-content">
              {loadingInshortsLogs ? (
                <div className="developer-section-loading">
                  <Loader />
                </div>
              ) : inshortsLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                  <p>No Inshorts API logs found for this news</p>
                </div>
              ) : (
                <div className="inshorts-logs-list">
                  {inshortsLogs.map((log) => (
                    <div key={log.id} className="inshorts-log-item">
                      <div
                        className="inshorts-log-header"
                        onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span
                          className="inshorts-log-method"
                          style={{ background: getLogMethodColor(log.method) }}
                        >
                          {log.method || 'N/A'}
                        </span>
                        <span className="inshorts-log-endpoint">{log.endpoint || 'N/A'}</span>
                        <span className="inshorts-log-timestamp">{formatLogTimestamp(log.timestamp)}</span>
                        {expandedLogId === log.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>

                      {expandedLogId === log.id && (
                        <div className="inshorts-log-details">
                          <div className="developer-json-block">
                            <div className="developer-json-header">
                              <h4>CURL</h4>
                              <button
                                className="developer-json-copy-button"
                                onClick={() => copyLogText(log.curl, `curl-${log.id}`)}
                                title={copiedLogId === `curl-${log.id}` ? 'Copied!' : 'Copy'}
                              >
                                {copiedLogId === `curl-${log.id}` ? <span className="copy-copied-text">Copied</span> : <Copy size={12} />}
                              </button>
                            </div>
                            <pre className="developer-json-content">{log.curl || 'N/A'}</pre>
                          </div>

                          <div className="developer-json-block">
                            <div className="developer-json-header">
                              <h4>Response</h4>
                              <button
                                className="developer-json-copy-button"
                                onClick={() => copyLogText(JSON.stringify(log.response, null, 2), `response-${log.id}`)}
                                title={copiedLogId === `response-${log.id}` ? 'Copied!' : 'Copy'}
                              >
                                {copiedLogId === `response-${log.id}` ? <span className="copy-copied-text">Copied</span> : <Copy size={12} />}
                              </button>
                            </div>
                            <pre className="developer-json-content">{JSON.stringify(log.response, null, 2)}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}
        </div>

        {cropSource && (
          <ImageCropper
            isOpen
            src={cropSource}
            onCancel={() => setCropSource(null)}
            onApply={handleApplyCrop}
          />
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Custom Tooltip */}
        {tooltip.visible && (
          <div
            className="custom-tooltip"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: 'translateX(-50%) translateY(-100%)',
            }}
          >
            <span className={tooltip.type === 'matched' ? 'tooltip-text-green' : 'tooltip-text-red'}>
              {tooltip.text}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default NewsDetail;
